import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "../Styles";
import { NewInput, NewSelect } from "../Components";
import { NewFieldStyles } from "../Definitions";
import { ValueType } from "./valueTypes";

/* eslint-disable */
export enum FilterDisplayEnum {
  select = "select",
  range = "range",
}
/* eslint-enable */

export interface Field {
  id?: number,
  value?: ValueType[]
}

export interface DisplayType {
  id: number,
  label: string,
  displayType?: FilterDisplayEnum,
  options?: {
    label: string,
    id: number,
  }[],
  text?: string
}

export const FilterContent = (props: {filter: Field, index?: number, filterParameterTypes?: DisplayType[], handleFilterValueChange: (value: ValueType[]) => void, costumContent?: JSX.Element, operationsView?: boolean }) => { 
  const {t} = useTranslation()
  const [filterValues, setFilterValues] = useState<(Pick<ValueType, "id"> & Pick<Partial<ValueType>, "label">)[]>([]);
  const {filter, filterParameterTypes } = props;
  const filterParameter = filterParameterTypes?.find((filterParameterType) => filterParameterType.id === filter.id)
  const mobileXs = useMediaQuery(`(max-width: 793px)`);
  useEffect(() => {
    if (filter.value) {
      // always have one empty field at the end
      setFilterValues([...filter.value, { id: -1 }]);
    } else {
      setFilterValues([{ id: -1 }]);
    }
  }, [filter]);

  const handleFieldChange = (index: number, value: { id: number, label: string }) => {
    const updatedFields = [...filterValues];
    if (updatedFields[index].id === -1) {
      updatedFields.push({ id: -1 });
    }
    updatedFields[index] = value;
    
    setFilterValues(updatedFields);
    const filtersWithLabel = updatedFields.filter((filter) => !!filter.label) as ValueType[];
    
    props.handleFilterValueChange(filtersWithLabel);
  };

  // Delete field
  const handleFieldDelete = useCallback((index: number) => {
    const updatedSelectFields = [...filterValues];
    updatedSelectFields.splice(index, 1)
    setFilterValues(updatedSelectFields)
  },[filterValues]);
  
  // #region || Handling Animal No. input field

  const [tempInput, setTempInput] = useState<string>(filter.value ? formatRangeOutput(filter.value.map(({id}) => (id))) : "");
  // #endregion

  // Style wrapper for content
  const wrapperStyle = {
    boxSizing: "border-box", 
    display: "flex", 
    flexWrap: "wrap", 
    gap: "10px", 
    width: mobileXs ? "280px" : "570px"
  } as React.CSSProperties;
  
  if (!filterParameter?.displayType) {
    return null
  }
  
  switch (filterParameter.displayType) {
    case FilterDisplayEnum.select:
      return (
        <div style={{...wrapperStyle}}>
          {filterValues.map((field, fieldIndex) => {
            const selectedIds = filterValues
            .filter((f, i) => i !== fieldIndex && f.id !== undefined && f.id !== -1)
            .map((f) => f.id);
            const availableOptions = filterParameter.options?.filter((option) => !selectedIds.includes(option.id)) || [];
          
            return (
              <div key={field.id !== undefined ? `${field.id}-${fieldIndex}` : fieldIndex}>
                <NewSelect
                  key={fieldIndex + String(field.id)}
                  placeholder={t("select", { defaultValue: "select" })}
                  options={availableOptions || []}
                  onChange={(option) => { option &&
                    handleFieldChange(fieldIndex, { id: Number(option.id), label: option.label });
                  }}
                  valueOverrides={{
                    defaultValue: filterParameter.options?.find((option) => option.id === field.id),
                  }}
                  isClearable={field.id !== -1 ? true : false}
                  onClear={() => handleFieldDelete(fieldIndex)}
                  fieldOverrides={{
                    fieldStyle: props.operationsView ? NewFieldStyles.greySecondary : NewFieldStyles.greyTertiary 
                  }}
                />
              </div>
            )
          })}
        </div>
      )
      case FilterDisplayEnum.range:
        return (
          <div style={{ ...wrapperStyle }}>
            <NewInput
              placeholder={"1, 2, 5-9"}
              onChange={(e) => setTempInput(e)}
              onBlur={() => {
                const parsedValues = parseRangeInput(tempInput); // Parse inputtet, når feltet mister fokus
                props.handleFilterValueChange(parsedValues.map((number) => ({id: number, label: number.toString()}))); // Opdater quickEntryData
              }}
              valueOverrides={{
                defaultValue: tempInput,
              }}
              fieldOverrides={{
                fieldStyle: props.operationsView ? NewFieldStyles.greySecondary : NewFieldStyles.greyTertiary,
              }}
              isClearable
            />
          </div>
        );
    default:
    return null;
  }
}


// Handling input: Transform the new value to array of numbers
const parseRangeInput = (input: string): number[] => {
  if (!input.trim()) return []; 

  const values = input
    .split(",") 
    .map((part) => part.trim())
    .flatMap((value) => {
      if (value.includes("-")) {
        let [start, end] = value.split("-").map(Number);
        if (isNaN(start) || isNaN(end)) return [];
        if (start > end) [start, end] = [end, start];
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
      } else {
        const num = Number(value);
        return isNaN(num) ? [] : [num];
      }
    });

  return Array.from(new Set(values));
};

// Handling output: Transform the existing value from array of numbers
const formatRangeOutput = (values: number[]): string => {
  if (!values || values.length === 0) return "";

  const sortedValues = Array.from(new Set(values)).sort((a, b) => a - b);

  const ranges: string[] = [];
  let start = sortedValues[0];
  let end = start;

  for (let i = 1; i <= sortedValues.length; i++) {
    if (sortedValues[i] === end + 1) {
      end = sortedValues[i];
    } else {
      if (start === end) {
        ranges.push(`${start}`);
      } else {
        ranges.push(`${start}-${end}`);
      }
      start = sortedValues[i];
      end = start;
    }
  }

  return ranges.join(", ");
};